import React, { useState } from "react";
import {
  useTranslate,
  useNavigation,
  IResourceComponentsProps,
} from "@pankod/refine-core";
import {
  List,
  Table,
  Tag,
  Space,
  Button,
  useTable,
  DateField,
  ShowButton,
} from "@pankod/refine-antd";
import { PlusOutlined } from "@ant-design/icons";
import { IQuizAICreation, QuizAICreationStatus } from "interfaces";
import { QuizAICreationCreateModal } from "./create";

const STATUS_COLORS: Record<QuizAICreationStatus, string> = {
  pending: "default",
  processing: "processing",
  completed: "success",
  failed: "error",
};

export const QuizAICreationList: React.FC<IResourceComponentsProps> = () => {
  const t = useTranslate();
  const { show } = useNavigation();
  const [createVisible, setCreateVisible] = useState(false);

  const { tableProps } = useTable<IQuizAICreation>({
    resource: "quiz-ai-creation",
    initialSorter: [{ field: "created_at", order: "desc" }],
    queryOptions: {
      refetchInterval: (data: any) => {
        const rows: IQuizAICreation[] = data?.data || [];
        const hasActive = rows.some(
          (r) => r.status === "pending" || r.status === "processing"
        );
        return hasActive ? 5000 : false;
      },
    },
  });

  return (
    <List
      title={t("quiz-ai-creation.title")}
      headerProps={{
        extra: [
          <Button
            key="create"
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => setCreateVisible(true)}
          >
            {t("buttons.create")}
          </Button>,
        ],
      }}
    >
      <Table {...tableProps} rowKey="id" scroll={{ x: 1200 }}>
        <Table.Column
          dataIndex="created_at"
          title={t("quiz-ai-creation.fields.created_at")}
          render={(value) => <DateField value={value} format="LLL" />}
          sorter
        />
        <Table.Column<IQuizAICreation>
          dataIndex="name"
          title={t("quiz-ai-creation.fields.name")}
          render={(value: string, record) => (
            <Button
              type="link"
              style={{
                padding: 0,
                whiteSpace: "normal",
                textAlign: "left",
                height: "auto",
              }}
              onClick={() => show("quiz-ai-creation", record.id)}
            >
              {value}
            </Button>
          )}
        />
        <Table.Column
          dataIndex="lang"
          title={t("quiz-ai-creation.fields.lang")}
          render={(value: string) => <Tag>{value}</Tag>}
        />
        <Table.Column
          dataIndex="quiz_category_name"
          title={t("quiz-ai-creation.fields.quiz_category_name")}
        />
        <Table.Column
          dataIndex="model_string"
          title={t("quiz-ai-creation.fields.model_string")}
        />
        <Table.Column
          dataIndex="status"
          title={t("quiz-ai-creation.fields.status")}
          render={(value: QuizAICreationStatus) => (
            <Tag color={STATUS_COLORS[value]}>
              {t(`quiz-ai-creation.status.${value}`)}
            </Tag>
          )}
        />
        <Table.Column
          dataIndex="quizzes_created"
          title={t("quiz-ai-creation.fields.quizzes_created")}
        />
        <Table.Column<IQuizAICreation>
          title={t("table.actions")}
          dataIndex="actions"
          render={(_, record) => (
            <Space>
              <ShowButton hideText size="small" recordItemId={record.id} />
            </Space>
          )}
        />
      </Table>

      <QuizAICreationCreateModal
        visible={createVisible}
        onClose={() => setCreateVisible(false)}
      />
    </List>
  );
};
